import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form"]
  static values = { 
    hasAi: Boolean,
    submitText: String,
    hasErrors: Boolean
  }

  connect() {
    this.isSubmitting = false
    this.progressInterval = null
    this.messageInterval = null
    this.encouragementInterval = null
    
    // Store original submit button text
    this.submitButton = this.element.querySelector('input[type="submit"]')
    if (this.submitButton && !this.hasSubmitTextValue) {
      this.submitTextValue = this.submitButton.value
    }

    // Hide loading screen if there are errors (page was re-rendered due to validation errors)
    if (this.hasErrorsValue) {
      this.hide()
      this.resetForm()
    }
  }

  disconnect() {
    this.clearIntervals()
  }

  handleSubmit(event) {
    // Prevent multiple submissions
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    // Basic form validation - check for required fields
    const titleField = this.element.querySelector('input[name="event[title]"]')
    const themeField = this.element.querySelector('select[name="event[theme]"]')
    
    if (!titleField || !titleField.value.trim() || !themeField || !themeField.value) {
      // Let the form submit normally to show validation errors
      return
    }

    // Check if AI prompt is provided
    const aiPrompt = this.element.querySelector('textarea[name="event[ai_prompt]"]')
    const hasAiContent = aiPrompt && aiPrompt.value.trim().length > 0
    this.hasAiValue = hasAiContent

    // Only show loading screen for forms that are likely to succeed and take time
    if (hasAiContent || this.isFormComplete()) {
      // Mark as submitting
      this.isSubmitting = true
      
      // Show loading screen after a short delay to allow form to start submitting
      setTimeout(() => {
        this.displayLoadingScreen()
        this.disableForm()
        this.startProgressAnimation()
        this.startMessageCycling()
        this.startEncouragementCycling()
      }, 50)
    }
  }

  isFormComplete() {
    const requiredFields = [
      'input[name="event[title]"]',
      'select[name="event[theme]"]',
      'textarea[name="event[description]"]',
      'input[name="event[event_date]"]',
      'select[name="event[location]"]'
    ]
    
    return requiredFields.every(selector => {
      const field = this.element.querySelector(selector)
      return field && field.value.trim()
    })
  }

  displayLoadingScreen() {
    const loadingScreen = document.getElementById('loading-screen')
    const messagesData = document.getElementById('loading-messages')
    
    if (loadingScreen && messagesData) {
      // Update initial messages based on AI usage
      const titleElement = document.getElementById('loading-title')
      const messageElement = document.getElementById('loading-message')
      
      if (this.hasAiValue) {
        titleElement.textContent = messagesData.dataset.aiTitle
        messageElement.textContent = messagesData.dataset.aiMessage
      } else {
        titleElement.textContent = messagesData.dataset.regularTitle
        messageElement.textContent = messagesData.dataset.regularMessage
      }
      
      // Show the loading screen
      loadingScreen.classList.remove('hidden')
      
      // Trigger reflow and then fade in
      loadingScreen.offsetHeight
      loadingScreen.classList.remove('opacity-0')
      loadingScreen.classList.add('opacity-100')
    }
  }

  disableForm() {
    // Disable all form inputs
    const inputs = this.element.querySelectorAll('input, textarea, select, button')
    inputs.forEach(input => {
      input.disabled = true
    })

    // Update submit button text
    if (this.submitButton) {
      this.submitButton.value = this.hasAiValue ? 'AI Planning in Progress...' : 'Creating Event...'
    }
  }

  startProgressAnimation() {
    const progressBar = document.getElementById('progress-bar')
    if (!progressBar) return

    let progress = 10
    const maxProgress = this.hasAiValue ? 95 : 90 // Leave some room before completion
    const increment = this.hasAiValue ? 2 : 3
    const intervalTime = this.hasAiValue ? 800 : 600

    this.progressInterval = setInterval(() => {
      progress += increment
      if (progress > maxProgress) {
        progress = maxProgress
        clearInterval(this.progressInterval)
      }
      progressBar.style.width = `${progress}%`
    }, intervalTime)
  }

  startMessageCycling() {
    if (!this.hasAiValue) return // Only cycle messages for AI mode

    const messagesData = document.getElementById('loading-messages')
    const messageElement = document.getElementById('loading-message')
    
    if (!messagesData || !messageElement) return

    try {
      const messages = JSON.parse(messagesData.dataset.aiMessages)
      let messageIndex = 0

      this.messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % messages.length
        
        // Fade out
        messageElement.style.opacity = '0'
        
        setTimeout(() => {
          messageElement.textContent = messages[messageIndex]
          // Fade in
          messageElement.style.opacity = '1'
        }, 300)
      }, 3000)
    } catch (error) {
      console.warn('Failed to parse AI messages:', error)
    }
  }

  startEncouragementCycling() {
    const messagesData = document.getElementById('loading-messages')
    const encouragementElement = document.getElementById('encouragement')
    
    if (!messagesData || !encouragementElement) return

    try {
      const encouragements = JSON.parse(messagesData.dataset.encouragements)
      let encouragementIndex = 0

      this.encouragementInterval = setInterval(() => {
        encouragementIndex = (encouragementIndex + 1) % encouragements.length
        
        // Fade out
        encouragementElement.style.opacity = '0'
        
        setTimeout(() => {
          encouragementElement.textContent = encouragements[encouragementIndex]
          // Fade in
          encouragementElement.style.opacity = '1'
        }, 300)
      }, 4000)
    } catch (error) {
      console.warn('Failed to parse encouragements:', error)
    }
  }

  clearIntervals() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
    if (this.messageInterval) {
      clearInterval(this.messageInterval)
      this.messageInterval = null
    }
    if (this.encouragementInterval) {
      clearInterval(this.encouragementInterval)
      this.encouragementInterval = null
    }
  }

  hide() {
    const loadingScreen = document.getElementById('loading-screen')
    if (loadingScreen) {
      loadingScreen.classList.remove('opacity-100')
      loadingScreen.classList.add('opacity-0')
      
      // Hide completely after transition
      setTimeout(() => {
        loadingScreen.classList.add('hidden')
      }, 300)
    }
    
    this.clearIntervals()
    this.isSubmitting = false
  }

  resetForm() {
    // Re-enable all form inputs
    const inputs = this.element.querySelectorAll('input, textarea, select, button')
    inputs.forEach(input => {
      input.disabled = false
    })

    // Reset submit button text
    if (this.submitButton && this.submitTextValue) {
      this.submitButton.value = this.submitTextValue
    }

    this.isSubmitting = false
  }

  // Handle page navigation away (cleanup)
  beforeUnload() {
    this.clearIntervals()
  }
}